import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrandFavicon } from '../BrandFavicon';
import { RelationshipBadge } from '../visibility/VisibilityBadges';
import { resolvePromptTopBrandsDisplay, sortPromptListBrands } from '../../utils/prompt-top-brands';
import { resolveAiMentions } from '../../utils/visibility-metrics';
import type { PromptListStatus, PromptListTopBrand } from '../../types';
import { TopBrandAvatars } from './TopBrandAvatars';

function brandKey(brand: PromptListTopBrand): string {
  return brand.knownBrandId ?? brand.canonicalName.toLowerCase();
}

function BrandDetailRow({ brand }: { brand: PromptListTopBrand }) {
  const aiMentions = resolveAiMentions(brand);

  return (
    <div className="d-flex align-items-start gap-2 px-2 py-2 rounded-2 border bg-body-tertiary">
      <BrandFavicon
        host={brand.domain}
        faviconUrl={brand.faviconUrl}
        name={brand.canonicalName}
        relationship={brand.relationship}
        colorIndex={0}
        size="xs"
      />
      <div className="min-w-0 flex-grow-1">
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <p className="fs--1 fw-semibold text-1000 text-truncate mb-0">{brand.canonicalName}</p>
          <RelationshipBadge relationship={brand.relationship} className="fs--2" />
        </div>
        <p className="fs--2 text-700 mb-0 mt-1">
          منشن {aiMentions}
          {' · '}
          رتبه {brand.avgPosition != null ? `#${brand.avgPosition}` : '—'}
          {' · '}
          دیده‌شدن {brand.visibilityScore}%
          {' · '}
          کاوریج {brand.coverage}
        </p>
      </div>
    </div>
  );
}

export function PromptTopBrands({
  status = 'executed',
  runs,
  brands,
  brandsFound,
  max = 3,
}: {
  status?: PromptListStatus;
  runs?: number;
  brands: PromptListTopBrand[];
  brandsFound?: number;
  max?: number;
}) {
  const effectiveRuns = runs ?? (brands.length > 0 || (brandsFound ?? 0) > 0 ? 1 : 0);
  const display = resolvePromptTopBrandsDisplay(status, effectiveRuns, sortPromptListBrands(brands));
  const [hover, setHover] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<number | null>(null);

  function clearHideTimeout() {
    if (hideTimeoutRef.current != null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }

  function updateTooltipPosition() {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const tooltipHeight = Math.min(420, window.innerHeight * 0.55);
    const tooltipWidth = Math.min(280, window.innerWidth - 32);
    const rightPosition = rect.right + 8;
    // Open toward the right when possible; keep the panel visible near the viewport edge.
    const opensRight = rightPosition + tooltipWidth <= window.innerWidth - 16;
    const left = opensRight
      ? rightPosition
      : Math.max(16, rect.left - tooltipWidth - 8);
    const maxTop = Math.max(16, window.innerHeight - tooltipHeight - 16);

    setTooltipPos({
      left,
      top: Math.min(Math.max(16, rect.top), maxTop),
    });
  }

  function openTooltip() {
    clearHideTimeout();
    updateTooltipPosition();
    setHover(true);
  }

  function scheduleClose() {
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      setHover(false);
      setTooltipPos(null);
    }, 150);
  }

  useEffect(() => () => clearHideTimeout(), []);

  useLayoutEffect(() => {
    if (!hover) return;

    updateTooltipPosition();

    function handleReposition() {
      updateTooltipPosition();
    }

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [hover, brands.length]);

  if (display.kind === 'not-run') {
    return <span className="fs--1 text-300">—</span>;
  }

  if (display.kind === 'no-brands') {
    return <span className="fs--1 text-700">برندی شناسایی نشد</span>;
  }

  const sorted = display.brands;

  const tooltip =
    hover && tooltipPos
      ? createPortal(
          <div
            className="position-fixed rounded-3 border bg-white dark__bg-card-dark shadow-lg text-start"
            dir="rtl"
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
              zIndex: 1100,
              width: Math.min(280, window.innerWidth - 32),
            }}
            onMouseEnter={openTooltip}
            onMouseLeave={scheduleClose}
          >
            <div className="p-2">
              <p className="px-1 mb-1 fs--2 fw-semibold text-uppercase text-700">برندهای برتر</p>
              <div className="d-flex flex-column gap-2" style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                {sorted.map(brand => (
                  <BrandDetailRow key={brandKey(brand)} brand={brand} />
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={triggerRef}
      className="position-relative d-inline-flex"
      onMouseEnter={openTooltip}
      onMouseLeave={scheduleClose}
    >
      <TopBrandAvatars
        brands={sorted}
        brandsFound={brandsFound ?? sorted.length}
        max={max}
        showHoverTooltip={false}
      />
      {tooltip}
    </div>
  );
}
